import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertCircle, CheckCircle, Key as KeyIcon, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuthErrorMessage } from '../lib/auth/errors';
import { supabase } from '../lib/supabase';
import { validatePassword } from '../lib/auth/validation';

/**
 * Parse les paramètres d'erreur du hash de l'URL
 * Format: #error=access_denied&error_code=otp_expired&error_description=...
 */
function parseHashError(): { error: string; errorCode: string; errorDescription: string } | null {
  const hash = window.location.hash;
  if (!hash || !hash.includes('error=')) {
    return null;
  }

  const params = new URLSearchParams(hash.substring(1));
  const error = params.get('error');
  const errorCode = params.get('error_code');
  const errorDescription = params.get('error_description');

  if (error) {
    return {
      error,
      errorCode: errorCode || '',
      errorDescription: errorDescription
        ? decodeURIComponent(errorDescription.replace(/\+/g, ' '))
        : '',
    };
  }

  return null;
}

/**
 * Parse les tokens de session du hash de l'URL
 * Format: #access_token=xxx&refresh_token=xxx&type=recovery...
 */
function parseHashTokens(): { accessToken: string; refreshToken: string } | null {
  const hash = window.location.hash;
  if (!hash || !hash.includes('access_token=')) {
    return null;
  }

  const params = new URLSearchParams(hash.substring(1));
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');

  if (accessToken && refreshToken) {
    return { accessToken, refreshToken };
  }

  return null;
}

function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [linkError, setLinkError] = useState<{
    error: string;
    errorCode: string;
    errorDescription: string;
  } | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const initializeSession = async () => {
      // Vérifier d'abord s'il y a une erreur dans l'URL (lien expiré, etc.)
      const hashError = parseHashError();
      if (hashError) {
        setLinkError(hashError);
        setIsInitializing(false);
        // Nettoyer l'URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      // Vérifier s'il y a des tokens dans le hash
      const tokens = parseHashTokens();
      if (tokens) {
        // Créer la session manuellement avec les tokens du hash
        const { error } = await supabase.auth.setSession({
          access_token: tokens.accessToken,
          refresh_token: tokens.refreshToken,
        });

        if (error) {
          console.error('[ResetPasswordPage] Failed to set session:', error);
          setLinkError({
            error: 'session_error',
            errorCode: 'session_failed',
            errorDescription: 'Impossible de créer la session. Le lien est peut-être expiré.',
          });
        }

        // Nettoyer l'URL
        window.history.replaceState({}, document.title, window.location.pathname);
        setIsInitializing(false);
        return;
      }

      // Vérifier si on a déjà une session active (nécessaire pour valider le token)
      await supabase.auth.getSession();

      setIsInitializing(false);
    };

    initializeSession();
  }, []);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    // Réinitialiser le message quand l'utilisateur tape
    if (message && !isLoading && !isSuccess) {
      setMessage('');
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    // Réinitialiser le message quand l'utilisateur tape
    if (message && !isLoading && !isSuccess) {
      setMessage('');
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation côté client avec les fonctions centralisées
    const passwordError = validatePassword(password);
    if (passwordError) {
      setMessage(passwordError);
      setIsSuccess(false);
      return;
    }

    if (!confirmPassword.trim()) {
      setMessage('Veuillez confirmer votre mot de passe');
      setIsSuccess(false);
      return;
    }

    if (password !== confirmPassword) {
      setMessage('Les mots de passe ne correspondent pas');
      setIsSuccess(false);
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      // Vérifier qu'on a une session active
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        setMessage('Erreur de session. Veuillez demander un nouveau lien de réinitialisation.');
        setIsSuccess(false);
        return;
      }

      if (!sessionData.session) {
        setMessage('Session expirée. Veuillez demander un nouveau lien de réinitialisation.');
        setIsSuccess(false);
        return;
      }

      // Mettre à jour le mot de passe
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        setMessage(getAuthErrorMessage(error));
        setIsSuccess(false);
      } else {
        setIsSuccess(true);
        // Redirection après 5 secondes pour laisser le temps de lire
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 5000);
      }
    } catch {
      setMessage("Une erreur inattendue s'est produite. Veuillez réessayer plus tard");
      setIsSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Écran de chargement pendant l'initialisation de la session
  if (isInitializing) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="flex flex-col gap-6 items-center justify-start">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900"></div>
          <p className="text-zinc-500 text-sm">Vérification du lien...</p>
        </div>
      </div>
    );
  }

  // Écran d'erreur si le lien est invalide ou expiré
  if (linkError) {
    const isExpired = linkError.errorCode === 'otp_expired';

    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="flex flex-col gap-6 items-center justify-start max-w-[24rem] w-[24rem]">
          {/* Icône d'erreur */}
          <AlertCircle className="size-8 text-amber-500" />

          {/* Contenu principal */}
          <div className="flex flex-col gap-3 items-start justify-start w-full">
            {/* Titre */}
            <div className="font-bold text-[1.875rem] text-zinc-950 w-full text-center leading-[2.25rem]">
              {isExpired ? 'Lien expiré' : 'Lien invalide'}
            </div>

            {/* Description */}
            <div className="font-normal text-[0.875rem] text-zinc-500 w-full text-center leading-[1.25rem]">
              {isExpired ? (
                <p>
                  Ce lien de réinitialisation a expiré. Les liens sont valides pendant 1 heure après
                  leur création.
                </p>
              ) : (
                <p>Ce lien de réinitialisation est invalide ou a déjà été utilisé.</p>
              )}
            </div>

            {/* Bouton pour demander un nouveau lien */}
            <Button
              onClick={() => navigate('/forgot-password', { replace: true })}
              className="bg-zinc-900 hover:bg-zinc-800 text-neutral-50 h-9 px-4 py-2 rounded-[0.375rem] shadow-[0px_0.0625rem_0.1875rem_0px_rgba(0,0,0,0.1),0px_0.0625rem_0.125rem_0px_rgba(0,0,0,0.06)] w-full font-medium text-[0.875rem] leading-[1.25rem] mt-3"
            >
              Demander un nouveau lien
            </Button>
          </div>

          {/* Lien retour connexion */}
          <div className="flex flex-col gap-6 items-center justify-start w-full">
            <Link
              to="/login"
              className="font-normal text-[0.875rem] text-zinc-500 underline hover:text-zinc-700 transition-colors"
            >
              Retourner sur l'écran de connexion
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Écran de succès après changement de mot de passe - Design Figma
  if (isSuccess) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="flex flex-col gap-[1.5rem] items-center justify-start max-w-[24rem] w-[24rem]">
          {/* Icône de check */}
          <CheckCircle className="size-8 text-green-600" />

          {/* Contenu principal */}
          <div className="flex flex-col gap-[0.75rem] items-start justify-start w-full">
            {/* Titre */}
            <div className="font-bold text-[1.875rem] text-zinc-950 w-full text-center leading-[2.25rem]">
              Mot de passe rénitialisé
            </div>

            {/* Description */}
            <div className="font-normal text-[0.875rem] text-zinc-500 w-full text-center leading-[1.25rem]">
              <p className="mb-0">Votre mot de passe a été réinitialisé</p>
              <p>&nbsp;</p>
            </div>

            {/* Bouton Continuer */}
            <Button
              onClick={() => navigate('/login', { replace: true })}
              className="bg-zinc-900 hover:bg-zinc-800 text-neutral-50 h-[2.25rem] px-[1rem] py-[0.5rem] rounded-[0.375rem] shadow-[0px_0.0625rem_0.1875rem_0px_rgba(0,0,0,0.1),0px_0.0625rem_0.125rem_0px_rgba(0,0,0,0.06)] w-full font-medium text-[0.875rem] leading-[1.25rem]"
            >
              Continuer
            </Button>
          </div>

          {/* Section de liens */}
          <div className="flex flex-col gap-[1.5rem] items-center justify-start rounded-[0.5rem] w-full">
            <div className="flex flex-col gap-[1.5rem] items-center justify-start w-full">
              <div className="flex flex-col gap-[1.5rem] items-center justify-start w-full">
                <Link
                  to="/login"
                  className="font-normal text-[0.875rem] text-zinc-500 underline hover:text-zinc-700 transition-colors text-right"
                >
                  Retourner sur l'écran de connexion
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen flex items-center justify-center">
      <div className="flex flex-col gap-6 items-center justify-start max-w-[24rem] w-[24rem]">
        {/* Key Icon */}
        <KeyIcon className="size-8 text-zinc-600" />

        {/* Header */}
        <div className="flex flex-col gap-3 items-start justify-start text-center w-full">
          <div className="font-bold text-[1.875rem] text-zinc-950 w-full leading-[2.25rem]">
            Création d'un nouveau mot de passe
          </div>
          <div className="font-normal text-[0.875rem] text-zinc-500 w-full leading-[1.25rem]">
            Veuillez entrer votre nouveau mot de passe
          </div>
        </div>

        {/* Form */}
        <div className="flex flex-col gap-6 items-center justify-start rounded-[0.5rem] w-full">
          <form
            onSubmit={handleUpdatePassword}
            className="flex flex-col gap-2 items-start justify-start w-full"
          >
            {/* Password Input */}
            <div className="flex flex-col gap-2 items-start justify-start w-full">
              <div className="bg-white h-9 relative rounded-[0.375rem] w-full">
                <Input
                  type="password"
                  placeholder="Mot de passe"
                  value={password}
                  onChange={handlePasswordChange}
                  disabled={isLoading}
                  className="h-9 px-3 py-1 border border-zinc-200 rounded-[0.375rem] shadow-[0px_0.0625rem_0.125rem_0px_rgba(0,0,0,0.05)] text-[0.875rem] placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                />
              </div>
            </div>

            {/* Confirm Password Input */}
            <div className="flex flex-col gap-2 items-start justify-start w-full">
              <div className="bg-white h-9 relative rounded-[0.375rem] w-full">
                <Input
                  type="password"
                  placeholder="Confirmer le mot de passe"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  disabled={isLoading}
                  className="h-9 px-3 py-1 border border-zinc-200 rounded-[0.375rem] shadow-[0px_0.0625rem_0.125rem_0px_rgba(0,0,0,0.05)] text-[0.875rem] placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                />
              </div>
            </div>
          </form>

          {/* Button and Back Link */}
          <div className="flex flex-col gap-6 items-center justify-start w-full">
            <Button
              type="submit"
              onClick={handleUpdatePassword}
              className="bg-zinc-900 hover:bg-zinc-800 text-neutral-50 h-9 px-4 py-2 rounded-[0.375rem] shadow-[0px_0.0625rem_0.1875rem_0px_rgba(0,0,0,0.1),0px_0.0625rem_0.125rem_0px_rgba(0,0,0,0.06)] w-full font-medium text-[0.875rem] leading-[1.25rem]"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Réinitialisation...
                </>
              ) : (
                'Réinitialiser le mot de passe'
              )}
            </Button>

            {/* Back to Login Link */}
            <div className="flex flex-col gap-6 items-center justify-start w-full">
              <div className="flex flex-col gap-6 items-center justify-start w-full">
                <Link
                  to="/login"
                  className="font-normal text-[0.875rem] text-zinc-500 underline hover:text-zinc-700 transition-colors"
                >
                  Retourner sur l'écran de connexion
                </Link>
              </div>
            </div>
          </div>

          {/* Message de feedback */}
          {message && (
            <div
              className={`text-sm p-3 rounded-md w-full fade-in ${
                isSuccess
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              <div className="flex items-start gap-2">
                {isSuccess ? (
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                )}
                <span>{message}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
